import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AsyncSelect } from '@/components/ui/async-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { DateInput } from '@/components/ui/date-input';
import { Icon } from '@/components/ui/icon';
import {
  Stepper,
  StepperContent,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from '@/components/ui/stepper';
import { useRouter } from '@/hooks/use-router';
import { apiClient } from '@/lib/api-client';
import { formatDateOnly } from '@/lib/date';
import { applyBestMask } from '@/lib/mask';
import { deriveShirtSizeId, formatSizeBreakdown } from '@/lib/sale-item-sizes';
import { SALE_STATUSES, STATUS_LABELS } from '@/lib/sale-status';
import { cn } from '@/lib/utils';
import {
  useDynamicCreate,
  useDynamicDelete,
  useDynamicList,
  useDynamicUpdate,
} from '@/services/dynamic-resource';
import {
  type AdditionalOption,
  applyOptionsSurcharge,
  type DraftItem,
  ItemForm,
} from './item-form';
import { NewCustomerModal } from './new-customer-modal';
import { DeliveryBadge, StatusBadge } from './status';
import {
  StatusPillSelect,
  ToggleChip,
  WizardField,
  WizardInput,
  WizardSelect,
  WizardStepCard,
  wizardControlClass,
} from './wizard-fields';

// Todo status dá pra escolher aqui, exceto "Cancelado" — cancelamento é uma ação
// separada (fora do wizard), não algo que se define ajustando esse campo.
const STATUS_OPTIONS = SALE_STATUSES.filter((status) => status !== 'canceled').map(
  (status) => ({
    label: STATUS_LABELS[status] ?? status,
    value: status as string,
  }),
);

const DEPOSIT_TYPE_OPTIONS = [
  { label: 'Porcentagem', value: 'percentage' },
  { label: 'Valor fixo', value: 'fixed' },
];

function money(value: number) {
  return `R$ ${(value || 0).toFixed(2)}`;
}

function itemOptionLabels(item: DraftItem, options: AdditionalOption[]) {
  return options.filter((option) => item.selectedOptions.includes(option.id));
}

interface CustomerLite {
  id: string;
  name: string;
  email: string;
  phone: string;
  documentNumber?: string;
  zipCode?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
}

/** Monta "Rua, 123 - Bairro, Cidade/UF · CEP 00000-000" a partir do que o cliente tiver preenchido. */
function formatCustomerAddress(customer: CustomerLite) {
  const line1 = [customer.street, customer.number].filter(Boolean).join(', ');
  const line1WithNeighborhood = [line1, customer.neighborhood]
    .filter(Boolean)
    .join(' - ');
  const cityState = [customer.city, customer.state].filter(Boolean).join('/');
  const parts = [line1WithNeighborhood, cityState].filter(Boolean).join(', ');
  const withComplement = customer.complement
    ? `${parts} (${customer.complement})`
    : parts;
  const withZip = customer.zipCode
    ? `${withComplement}${withComplement ? ' · CEP ' : 'CEP '}${customer.zipCode}`
    : withComplement;

  return withZip || null;
}

function useResourceSearch(resource: string) {
  return (params?: { search?: string }) =>
    useQuery({
      queryKey: ['sales-wizard-search', resource, params?.search],
      queryFn: async () => {
        const res = await (apiClient as any)[resource].get({
          query: { search: params?.search, perPage: 50 },
        });
        return (res.data?.data ?? []) as any[];
      },
    });
}

export interface SaleWizardValues {
  customerId: string;
  paymentMethodId: string;
  companyId: string;
  status: string;
  deliveryDate: string;
  depositType: string;
  depositValue: number;
}

interface SaleWizardProps {
  mode: 'create' | 'edit';
  saleId?: string;
  saleNumber?: string;
  initialCustomer?: CustomerLite | null;
  initialValues?: SaleWizardValues;
  initialItems?: DraftItem[];
  initialSaleOptions?: string[];
}

/** Assistente de pedido em 4 passos — usado tanto pra criar quanto pra editar (só enquanto pendente). */
export function SaleWizard({
  mode,
  saleId,
  saleNumber,
  initialCustomer,
  initialValues,
  initialItems,
  initialSaleOptions,
}: SaleWizardProps) {
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState<CustomerLite | null>(initialCustomer ?? null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [items, setItems] = useState<DraftItem[]>(initialItems ?? []);
  const [editingItem, setEditingItem] = useState<DraftItem | null>(null);
  const [duplicatingItem, setDuplicatingItem] = useState<DraftItem | null>(null);
  const [saleOptionIds, setSaleOptionIds] = useState<string[]>(initialSaleOptions ?? []);

  const { data: paymentMethods } = useDynamicList('payment-methods', { perPage: 100 });
  const { data: companiesResponse } = useDynamicList('companies', {
    isActive: 'true',
    perPage: 100,
  });
  const { data: itemOptionsResponse } = useDynamicList('additional-options', {
    scope: 'item',
    isActive: 'true',
    perPage: 100,
  });
  const { data: saleOptionsResponse } = useDynamicList('additional-options', {
    scope: 'sale',
    isActive: 'true',
    perPage: 100,
  });
  const { data: operationalCostsResponse } = useDynamicList('operational-costs', {
    isActive: 'true',
    perPage: 100,
  });
  const itemOptions: AdditionalOption[] = itemOptionsResponse?.data ?? [];
  const saleOptions: AdditionalOption[] = saleOptionsResponse?.data ?? [];
  const operationalCostTiers: {
    minQuantity: number;
    maxQuantity: number | null;
    price: number;
  }[] = operationalCostsResponse?.data ?? [];

  const { mutate: createSale, isPending: isSavingSale } = useDynamicCreate('sales');
  const { mutate: updateSale, isPending: isUpdatingSale } = useDynamicUpdate('sales');
  const { mutate: createSaleItem } = useDynamicCreate('sale-items');
  const { mutate: deleteSaleItem } = useDynamicDelete('sale-items');

  const form = useForm<SaleWizardValues>({
    defaultValues: initialValues ?? {
      status: 'pending',
      deliveryDate: '',
      customerId: '',
      paymentMethodId: '',
      companyId: '',
      depositType: 'percentage',
      depositValue: 50,
    },
  });

  const useCustomerSearch = useResourceSearch('customers');

  const itemsTotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
  const optionsSurcharge = applyOptionsSurcharge(itemsTotal, saleOptions, saleOptionIds);
  const totalShirtQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const operationalCostTier = operationalCostTiers.find(
    (tier) =>
      totalShirtQuantity >= tier.minQuantity &&
      (tier.maxQuantity == null || totalShirtQuantity <= tier.maxQuantity),
  );
  const operationalCost = operationalCostTier?.price ?? 0;
  const total = itemsTotal + optionsSurcharge + operationalCost;

  const status = form.watch('status');
  const deliveryDate = form.watch('deliveryDate');
  const depositType = form.watch('depositType');
  const depositValue = form.watch('depositValue');
  const depositAmount =
    depositType === 'percentage'
      ? total * ((depositValue || 0) / 100)
      : depositValue || 0;

  const toggleSaleOption = (optionId: string, checked: boolean) => {
    setSaleOptionIds((prev) =>
      checked ? [...prev, optionId] : prev.filter((id) => id !== optionId),
    );
  };

  const canGoToStep2 = !!form.watch('customerId') && !!form.watch('companyId');
  const canGoToStep3 =
    canGoToStep2 && !!form.watch('paymentMethodId') && !!form.watch('deliveryDate');
  const canGoToStep4 = canGoToStep3 && items.length > 0;

  const isSaving = isSavingSale || isUpdatingSale;

  const handleSaveItem = (item: DraftItem) => {
    setItems((prev) => {
      const exists = prev.some((i) => i.key === item.key);
      return exists ? prev.map((i) => (i.key === item.key ? item : i)) : [...prev, item];
    });
    setEditingItem(null);
    setDuplicatingItem(null);
  };

  const handleRemoveItem = (key: string) =>
    setItems((prev) => prev.filter((i) => i.key !== key));

  const saveItems = (targetSaleId: string) => {
    items.forEach((item) => {
      createSaleItem({
        saleId: targetSaleId,
        shirtTypeId: item.shirtTypeId,
        sleeveTypeId: item.sleeveTypeId,
        collarTypeId: item.collarTypeId,
        fabricTypeId: item.fabricTypeId,
        printTypeId: item.printTypeId,
        shirtTypePrice: item.shirtTypePrice,
        sleeveTypePrice: item.sleeveTypePrice,
        collarTypePrice: item.collarTypePrice,
        fabricTypePrice: item.fabricTypePrice,
        printTypePrice: item.printTypePrice,
        unitaryPrice: item.unitaryPrice,
        totalPrice: item.totalPrice,
        quantity: item.quantity,
        selectedOptions: item.selectedOptions,
        shirtSizeId: deriveShirtSizeId(item.sizeBreakdown),
        sizeBreakdown: item.sizeBreakdown,
        artworkPath: item.artworkPath,
      });
    });
  };

  const handleSubmit = () => {
    const {
      customerId,
      paymentMethodId,
      companyId,
      status,
      deliveryDate,
      depositType,
      depositValue,
    } = form.getValues();

    const payload = {
      customerId,
      paymentMethodId,
      companyId,
      status,
      deliveryDate,
      total,
      depositType,
      depositValue,
      depositAmount,
      operationalCost,
      selectedOptions: saleOptionIds,
    };

    if (mode === 'create') {
      createSale(
        { ...payload, saleDate: new Date().toISOString() },
        {
          onSuccess: (sale: any) => {
            saveItems(sale.id);
            router.push(`/sales/${sale.id}/view`);
          },
        },
      );
      return;
    }

    // edit: atualiza o pedido e recria os itens do zero (mais simples e seguro que
    // tentar diffar item a item, já que a lista inteira já está em memória aqui).
    updateSale(
      { id: saleId!, data: payload },
      {
        onSuccess: () => {
          (initialItems ?? []).forEach((item) => deleteSaleItem(item.key));
          saveItems(saleId!);
          router.push(`/sales/${saleId}/view`);
        },
      },
    );
  };

  const label = mode === 'create' ? 'Novo Pedido' : 'Editar Pedido';
  const companyOptions = (companiesResponse?.data ?? []).map((c: any) => ({
    label: c.tradeName,
    value: c.id,
  }));
  const paymentMethodOptions = (paymentMethods?.data ?? []).map((pm: any) => ({
    label: pm.description,
    value: pm.id,
  }));

  return (
    <Content
      breadcrumbs={{
        title: label,
        items: [
          { label: 'Listagem', route: '/sales' },
          { label: mode === 'create' ? 'Novo' : 'Editar', active: true },
        ],
      }}
    >
      <div className="mx-auto space-y-6">
        <Stepper value={step} onValueChange={setStep} className="space-y-6">
          <div className="rounded-[18px] border border-border-strong bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-[20px] font-extrabold tracking-[-0.01em]">
                    {mode === 'create' ? 'Novo Pedido' : `Pedido ${saleNumber ?? ''}`}
                  </h1>
                  {mode === 'edit' && <StatusBadge status={status} />}
                  {mode === 'edit' && deliveryDate && (
                    <DeliveryBadge deliveryDate={deliveryDate} status={status} />
                  )}
                </div>
                <p className="mt-1 truncate text-[13px] text-muted-foreground">
                  {customer?.name ?? 'Selecione um cliente'} · {totalShirtQuantity} peças
                  · alterações salvas apenas ao finalizar a revisão
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                icon="x"
                onClick={() => router.push('/sales')}
              >
                Descartar
              </Button>
            </div>

            <StepperNav className="mt-6">
              <StepperItem step={1}>
                <StepperTrigger>
                  <StepperIndicator>1</StepperIndicator>
                  <StepperTitle>Cliente</StepperTitle>
                </StepperTrigger>
                <StepperSeparator />
              </StepperItem>
              <StepperItem step={2}>
                <StepperTrigger disabled={!canGoToStep2}>
                  <StepperIndicator>2</StepperIndicator>
                  <StepperTitle>Pagamento</StepperTitle>
                </StepperTrigger>
                <StepperSeparator />
              </StepperItem>
              <StepperItem step={3}>
                <StepperTrigger disabled={!canGoToStep3}>
                  <StepperIndicator>3</StepperIndicator>
                  <StepperTitle>Itens</StepperTitle>
                </StepperTrigger>
                <StepperSeparator />
              </StepperItem>
              <StepperItem step={4}>
                <StepperTrigger disabled={!canGoToStep4}>
                  <StepperIndicator>4</StepperIndicator>
                  <StepperTitle>Revisão</StepperTitle>
                </StepperTrigger>
              </StepperItem>
            </StepperNav>
          </div>

          <StepperContent value={1}>
            <WizardStepCard
              icon="user"
              title="Dados do cliente"
              subtitle="Escolha a empresa e o cliente responsável pelo pedido."
              stepLabel="ETAPA 1 DE 4"
              footer={
                <>
                  <span className="text-[12px] text-muted-foreground">
                    Campos com <span className="text-destructive">*</span> são
                    obrigatórios.
                  </span>
                  <Button
                    type="button"
                    variant="primary"
                    icon="arrow-right"
                    iconPosition="right"
                    disabled={!canGoToStep2}
                    onClick={() => setStep(2)}
                  >
                    Continuar para pagamento
                  </Button>
                </>
              }
            >
              <div className="grid grid-cols-1 gap-5">
                <WizardField label="Empresa" required>
                  <WizardSelect
                    icon="building-2"
                    value={form.watch('companyId')}
                    onValueChange={(value) =>
                      form.setValue('companyId', value, { shouldValidate: true })
                    }
                    options={companyOptions}
                  />
                </WizardField>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <WizardField label="Cliente" required className="flex-1">
                    <Controller
                      name="customerId"
                      control={form.control}
                      render={({ field }) => (
                        <AsyncSelect
                          label="Cliente"
                          useQuery={useCustomerSearch}
                          getOptionValue={(c: any) => c.id}
                          getDisplayValue={(c: any) => c.name}
                          value={field.value}
                          onChange={(value, option) => {
                            field.onChange(value);
                            setCustomer((option as CustomerLite) ?? null);
                          }}
                          clearable
                          leadingIcon={<Icon name="search" size={16} />}
                          triggerClassName={cn(wizardControlClass, 'font-normal')}
                        />
                      )}
                    />
                  </WizardField>
                  <Button
                    type="button"
                    variant="outline"
                    icon="plus"
                    className="h-11 shrink-0 rounded-[12px] border-dashed border-primary-line bg-primary-soft text-primary hover:bg-primary-soft/80"
                    onClick={() => setCustomerModalOpen(true)}
                  >
                    Novo cliente
                  </Button>
                </div>

                {customer && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <WizardField label="Email">
                      <WizardInput icon="mail" value={customer.email} disabled />
                    </WizardField>
                    <WizardField label="Telefone">
                      <WizardInput
                        icon="phone"
                        mono
                        value={applyBestMask(customer.phone, [
                          '(99) 9999-9999',
                          '(99) 99999-9999',
                        ])}
                        disabled
                      />
                    </WizardField>
                    {customer.documentNumber && (
                      <WizardField label="CPF/CNPJ">
                        <WizardInput
                          icon="id-card"
                          mono
                          value={applyBestMask(customer.documentNumber, [
                            '999.999.999-99',
                            '99.999.999/9999-99',
                          ])}
                          disabled
                        />
                      </WizardField>
                    )}
                    <WizardField label="Endereço" hint="Usado na etiqueta de envio">
                      <WizardInput
                        icon="map-pin"
                        value={formatCustomerAddress(customer) ?? 'Não informado'}
                        disabled
                      />
                    </WizardField>
                  </div>
                )}
              </div>
            </WizardStepCard>
          </StepperContent>

          <StepperContent value={2}>
            <WizardStepCard
              icon="credit-card"
              title="Pagamento e prazo"
              subtitle="Defina como e quando este pedido será pago e entregue."
              stepLabel="ETAPA 2 DE 4"
              footer={
                <>
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Voltar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    icon="arrow-right"
                    iconPosition="right"
                    disabled={!canGoToStep3}
                    onClick={() => setStep(3)}
                  >
                    Continuar para itens
                  </Button>
                </>
              }
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="space-y-5">
                  <WizardField label="Forma de pagamento" required>
                    <WizardSelect
                      icon="wallet"
                      value={form.watch('paymentMethodId')}
                      onValueChange={(value) =>
                        form.setValue('paymentMethodId', value, { shouldValidate: true })
                      }
                      options={paymentMethodOptions}
                    />
                  </WizardField>
                  <WizardField label="Data de entrega" required>
                    <DateInput
                      icon="calendar"
                      aria-label="Data de entrega"
                      value={deliveryDate}
                      onChange={(value) =>
                        form.setValue('deliveryDate', value ?? '', {
                          shouldValidate: true,
                        })
                      }
                    />
                  </WizardField>
                </div>

                <div className="space-y-5">
                  <WizardField label="Status do pedido" required>
                    <StatusPillSelect
                      value={status}
                      onValueChange={(value) =>
                        form.setValue('status', value, { shouldValidate: true })
                      }
                      options={STATUS_OPTIONS}
                    />
                  </WizardField>

                  {mode === 'edit' &&
                    initialValues?.deliveryDate &&
                    deliveryDate !== initialValues.deliveryDate && (
                      <div className="flex items-start gap-2 rounded-[10px] border border-dashed border-border px-3.5 py-3 text-[12px] text-muted-foreground">
                        <Icon name="info" size={14} className="mt-0.5 shrink-0" />
                        <span>
                          Prazo original era{' '}
                          <strong className="text-destructive">
                            {formatDateOnly(initialValues.deliveryDate)}
                          </strong>
                          . Ao alterar a data, o cliente recebe um aviso automático no
                          WhatsApp.
                        </span>
                      </div>
                    )}
                </div>
              </div>
            </WizardStepCard>
          </StepperContent>

          <StepperContent value={3}>
            <WizardStepCard
              icon="shirt"
              title="Composição das peças"
              subtitle="Monte a camisa, defina quantidade e adicione ao pedido."
              stepLabel="ETAPA 3 DE 4"
              footer={
                <>
                  <Button type="button" variant="outline" onClick={() => setStep(2)}>
                    Voltar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={!canGoToStep4}
                    onClick={() => setStep(4)}
                  >
                    Revisar pedido
                  </Button>
                </>
              }
            >
              <div className="space-y-5">
                <ItemForm
                  editing={editingItem}
                  duplicateFrom={duplicatingItem}
                  itemNumber={items.length + 1}
                  onSave={handleSaveItem}
                  onCancelEdit={() => {
                    setEditingItem(null);
                    setDuplicatingItem(null);
                  }}
                />

                {items.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.08em] text-faint">
                      <span>Itens no pedido · {items.length}</span>
                    </div>

                    {items.map((item) => (
                      <div
                        key={item.key}
                        className="flex cursor-pointer items-center gap-3 rounded-[12px] border border-border bg-card px-3.5 py-3 transition-colors hover:border-border-strong"
                        onClick={() => setEditingItem(item)}
                      >
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-[9px] bg-primary-soft font-mono text-[13px] font-bold text-primary">
                          {item.quantity}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-medium">
                            {item.shirtTypeLabel} · {item.sleeveTypeLabel} ·{' '}
                            {item.collarTypeLabel} · {item.fabricTypeLabel} ·{' '}
                            {item.printTypeLabel}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            {formatSizeBreakdown(item.sizeBreakdown) ? (
                              <Badge variant="outline" className="font-mono">
                                {formatSizeBreakdown(item.sizeBreakdown)}
                              </Badge>
                            ) : (
                              <Badge variant="warning">Tamanho pendente</Badge>
                            )}
                            {itemOptionLabels(item, itemOptions).map((option) => (
                              <Badge key={option.id} variant="outline">
                                {option.label}
                              </Badge>
                            ))}
                            {item.artworkPath && (
                              <Badge variant="outline">Arte anexada</Badge>
                            )}
                          </div>
                        </div>
                        <div className="font-mono text-[14px] font-semibold">
                          {money(item.totalPrice)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            title="Duplicar e mudar tamanho"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingItem(null);
                              setDuplicatingItem(item);
                            }}
                          >
                            <Icon name="copy-plus" size={16} />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="hover:bg-destructive-soft hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveItem(item.key);
                            }}
                          >
                            <Icon name="trash" size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <div className="flex items-center justify-between border-t border-border pt-3">
                      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-faint">
                        Total dos itens
                      </span>
                      <span className="font-display text-[22px] font-extrabold tracking-[-0.01em]">
                        {money(itemsTotal)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </WizardStepCard>
          </StepperContent>

          <StepperContent value={4}>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
              <WizardStepCard
                icon="clipboard-check"
                title="Revisão final"
                subtitle="Confira os valores e a entrada antes de salvar as alterações."
                stepLabel="ETAPA 4 DE 4"
                footer={
                  <Button type="button" variant="outline" onClick={() => setStep(3)}>
                    Voltar
                  </Button>
                }
              >
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[10px] border border-border bg-border sm:grid-cols-4">
                    <MetaTile label="Cliente" value={customer?.name ?? '—'} />
                    <MetaTile
                      label="Pagamento"
                      value={
                        paymentMethodOptions.find(
                          (o: { value: string }) =>
                            o.value === form.watch('paymentMethodId'),
                        )?.label ?? '—'
                      }
                    />
                    <MetaTile
                      label="Status"
                      value={STATUS_OPTIONS.find((o) => o.value === status)?.label ?? '—'}
                    />
                    <MetaTile label="Entrega" value={formatDateOnly(deliveryDate)} mono />
                  </div>

                  <div className="overflow-hidden rounded-[10px] border border-border">
                    <div className="grid grid-cols-[1fr_64px_96px] bg-raised px-3 py-2 text-[9.5px] font-bold uppercase tracking-[0.08em] text-faint">
                      <span>Composição</span>
                      <span className="text-center">Qtd.</span>
                      <span className="text-right">Total</span>
                    </div>
                    {items.map((item) => (
                      <div
                        key={item.key}
                        className="grid grid-cols-[1fr_64px_96px] items-start border-t border-border px-3 py-2.5 text-sm"
                      >
                        <div className="min-w-0 pr-2">
                          <div className="font-medium">
                            {item.shirtTypeLabel} · {item.sleeveTypeLabel} ·{' '}
                            {item.collarTypeLabel} · {item.fabricTypeLabel} ·{' '}
                            {item.printTypeLabel}
                          </div>
                          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {formatSizeBreakdown(item.sizeBreakdown) ?? (
                              <span className="font-sans text-warning">
                                Tamanho pendente
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-center font-mono">{item.quantity}</div>
                        <div className="text-right font-mono font-medium">
                          {money(item.totalPrice)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {saleOptions.length > 0 && (
                    <WizardField label="Opções do pedido">
                      <div className="flex flex-wrap gap-2">
                        {saleOptions.map((option) => (
                          <ToggleChip
                            key={option.id}
                            checked={saleOptionIds.includes(option.id)}
                            onCheckedChange={(checked) =>
                              toggleSaleOption(option.id, checked)
                            }
                          >
                            {option.label}
                          </ToggleChip>
                        ))}
                      </div>
                    </WizardField>
                  )}

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <WizardField label="Tipo de entrada">
                      <WizardSelect
                        value={depositType}
                        onValueChange={(value) =>
                          form.setValue('depositType', value, { shouldValidate: true })
                        }
                        options={DEPOSIT_TYPE_OPTIONS}
                      />
                    </WizardField>
                    <WizardField
                      label={
                        depositType === 'percentage' ? 'Entrada (%)' : 'Entrada (R$)'
                      }
                    >
                      <WizardInput
                        mono
                        type="number"
                        value={depositValue ?? 0}
                        onChange={(e) =>
                          form.setValue('depositValue', Number(e.target.value) || 0)
                        }
                      />
                    </WizardField>
                  </div>
                </div>
              </WizardStepCard>

              <div className="h-fit space-y-1.5 rounded-[18px] border border-border bg-raised p-5 lg:sticky lg:top-[80px]">
                <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-faint">
                  Resumo financeiro
                </div>
                <div className="flex justify-between pt-2 text-[13px] text-muted-foreground">
                  <span>Itens</span>
                  <span className="font-mono">{money(itemsTotal)}</span>
                </div>
                {optionsSurcharge > 0 && (
                  <div className="flex justify-between text-[13px] text-muted-foreground">
                    <span>Opções adicionais</span>
                    <span className="font-mono">{money(optionsSurcharge)}</span>
                  </div>
                )}
                {operationalCost > 0 && (
                  <div className="flex justify-between text-[13px] text-muted-foreground">
                    <span>Custo operacional</span>
                    <span className="font-mono">{money(operationalCost)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-border-strong pt-2.5">
                  <span className="text-sm font-semibold">Total</span>
                  <span className="font-display text-[25px] font-extrabold tracking-[-0.02em]">
                    {money(total)}
                  </span>
                </div>

                <div className="mt-3 rounded-[12px] border border-primary-line bg-primary-soft p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                    Entrada (
                    {depositType === 'percentage'
                      ? `${depositValue || 0}%`
                      : 'valor fixo'}
                    )
                  </div>
                  <div className="mt-1 font-mono text-[16px] font-bold">
                    {money(depositAmount)}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                    Saldo de {money(total - depositAmount)} na entrega.
                  </div>
                </div>

                <Button
                  type="button"
                  variant="success"
                  icon="save"
                  disabled={isSaving}
                  className="mt-4 w-full"
                  onClick={handleSubmit}
                >
                  {mode === 'create' ? 'Confirmar pedido' : 'Salvar alterações'}
                </Button>
              </div>
            </div>
          </StepperContent>
        </Stepper>
      </div>

      <NewCustomerModal
        open={customerModalOpen}
        onOpenChange={setCustomerModalOpen}
        onCreated={(created) => {
          setCustomer(created);
          form.setValue('customerId', created.id);
        }}
      />
    </Content>
  );
}

function MetaTile({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="bg-card p-3">
      <div className="text-[9.5px] font-bold uppercase tracking-[0.08em] text-faint">
        {label}
      </div>
      <div
        className={`mt-0.5 truncate text-[13px] font-medium ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}
